import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, User, Phone, Mail, MapPin, IdCard, Shield, Verified, Copy, ExternalLink 
} from 'lucide-react';
import { toast } from 'react-toastify';
import { formatDate, formatPhone, formatDOB, getStatusColor } from '../../../utils/clientHelpers';

export default function ClientViewModal({ isOpen, client, onClose }) {
  if (!isOpen || !client) return null;

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  return (
    <AnimatePresence>
      {isOpen && client && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative w-full max-w-4xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden">
              {/* Header */}
              <div className="sticky top-0 z-10 bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex justify-center items-center w-12 h-12 bg-white/20 rounded-full backdrop-blur-sm">
                      <User className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">Client Details</h2>
                      <p className="text-sm text-emerald-100">Complete information</p>
                    </div>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={onClose}
                    className="p-2 text-white rounded-lg hover:bg-white/20 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </motion.button>
                </div>
              </div>

              {/* Content - Scrollable */}
              <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
                <div className="p-6 space-y-6">
                  {/* Personal Information Section */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200"
                  >
                    <div className="flex items-center mb-4">
                      <User className="w-5 h-5 text-blue-600 mr-2" />
                      <h3 className="text-lg font-bold text-blue-900">Personal Information</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Full Name</label>
                        <div className="mt-1 flex items-center justify-between p-3 bg-white rounded-lg">
                          <p className="text-sm font-semibold text-gray-900">
                            {client.firstName} {client.lastName}
                          </p>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => copyToClipboard(`${client.firstName} ${client.lastName}`)}
                            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                          >
                            <Copy className="w-4 h-4" />
                          </motion.button>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Gender</label>
                        <div className="mt-1 p-3 bg-white rounded-lg">
                          <p className="text-sm font-medium text-gray-900 capitalize">
                            {client.gender || 'N/A'}
                          </p>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Date of Birth</label>
                        <div className="mt-1 p-3 bg-white rounded-lg">
                          <p className="text-sm font-medium text-gray-900">
                            {formatDOB(client.dob)}
                          </p>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Status</label>
                        <div className="mt-1">
                          <span className={`inline-flex px-3 py-1.5 text-xs font-semibold rounded-full border-2 ${getStatusColor(client.status)}`}>
                            {client.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>

                  {/* Contact Information Section */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="p-6 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border border-purple-200"
                  >
                    <div className="flex items-center mb-4">
                      <Phone className="w-5 h-5 text-purple-600 mr-2" />
                      <h3 className="text-lg font-bold text-purple-900">Contact Information</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-semibold text-purple-700 uppercase tracking-wide">Email Address</label>
                        <div className="mt-1 flex items-center justify-between p-3 bg-white rounded-lg">
                          <div className="flex items-center flex-1 min-w-0">
                            <Mail className="w-4 h-4 text-purple-500 mr-2 flex-shrink-0" />
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {client.email || 'N/A'}
                            </p>
                          </div>
                          {client.email && (
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => copyToClipboard(client.email)}
                              className="ml-2 p-1 text-purple-600 hover:bg-purple-50 rounded flex-shrink-0"
                            >
                              <Copy className="w-4 h-4" />
                            </motion.button>
                          )}
                        </div>
                        {client.emailVerified && (
                          <div className="mt-1 flex items-center text-xs text-green-600">
                            <Verified className="w-3 h-3 mr-1" />
                            Email verified
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-purple-700 uppercase tracking-wide">Phone Number</label>
                        <div className="mt-1 flex items-center justify-between p-3 bg-white rounded-lg">
                          <div className="flex items-center flex-1 min-w-0">
                            <Phone className="w-4 h-4 text-purple-500 mr-2 flex-shrink-0" />
                            <p className="text-sm font-medium text-gray-900">
                              {formatPhone(client.phone)}
                            </p>
                          </div>
                          {client.phone && (
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => copyToClipboard(client.phone)}
                              className="ml-2 p-1 text-purple-600 hover:bg-purple-50 rounded flex-shrink-0"
                            >
                              <Copy className="w-4 h-4" />
                            </motion.button>
                          )}
                        </div>
                        {client.phoneVerified && (
                          <div className="mt-1 flex items-center text-xs text-green-600">
                            <Verified className="w-3 h-3 mr-1" />
                            Phone verified
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>

                  {/* Location Information Section */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="p-6 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl border border-emerald-200"
                  >
                    <div className="flex items-center mb-4">
                      <MapPin className="w-5 h-5 text-emerald-600 mr-2" />
                      <h3 className="text-lg font-bold text-emerald-900">Location Information</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Region</label>
                        <div className="mt-1 p-3 bg-white rounded-lg">
                          <p className="text-sm font-medium text-gray-900">
                            {client.region || 'N/A'}
                          </p>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Location</label>
                        <div className="mt-1 p-3 bg-white rounded-lg">
                          <p className="text-sm font-medium text-gray-900">
                            {client.location || 'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>

                  {/* ID Information Section */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="p-6 bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl border border-amber-200"
                  >
                    <div className="flex items-center mb-4">
                      <IdCard className="w-5 h-5 text-amber-600 mr-2" />
                      <h3 className="text-lg font-bold text-amber-900">ID Information</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Document Type</label>
                        <div className="mt-1 p-3 bg-white rounded-lg">
                          <p className="text-sm font-medium text-gray-900">
                            {client.idType || 'N/A'}
                          </p>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Card Number</label>
                        <div className="mt-1 flex items-center justify-between p-3 bg-white rounded-lg">
                          <p className="text-sm font-mono font-medium text-gray-900">
                            {client.cardNumber || 'N/A'}
                          </p>
                          {client.cardNumber && (
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => copyToClipboard(client.cardNumber)}
                              className="ml-2 p-1 text-amber-600 hover:bg-amber-50 rounded"
                            >
                              <Copy className="w-4 h-4" />
                            </motion.button>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Issue Date</label>
                        <div className="mt-1 p-3 bg-white rounded-lg">
                          <p className="text-sm font-medium text-gray-900">
                            {formatDate(client.issueDate)}
                          </p>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Expiry Date</label>
                        <div className="mt-1 p-3 bg-white rounded-lg">
                          <p className="text-sm font-medium text-gray-900">
                            {formatDate(client.expiryDate)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* ID Card Images */}
                    {(client.idCardFront || client.idCardBack) && (
                      <div className="mt-4">
                        <label className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2 block">ID Card Images</label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {client.idCardFront && (
                            <div className="relative group">
                              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-lg z-10 flex items-end justify-center p-2">
                                <motion.a
                                  href={client.idCardFront}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  className="px-3 py-1.5 bg-white rounded-lg text-xs font-semibold text-gray-900 flex items-center"
                                >
                                  <ExternalLink className="w-3 h-3 mr-1" />
                                  View Full Size
                                </motion.a>
                              </div>
                              <img
                                src={client.idCardFront}
                                alt="ID Card Front"
                                className="w-full h-48 object-cover rounded-lg border-2 border-amber-200 shadow-md"
                              />
                              <p className="text-xs text-center text-amber-700 mt-1 font-medium">Front</p>
                            </div>
                          )}
                          {client.idCardBack && (
                            <div className="relative group">
                              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-lg z-10 flex items-end justify-center p-2">
                                <motion.a
                                  href={client.idCardBack}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  className="px-3 py-1.5 bg-white rounded-lg text-xs font-semibold text-gray-900 flex items-center"
                                >
                                  <ExternalLink className="w-3 h-3 mr-1" />
                                  View Full Size
                                </motion.a>
                              </div>
                              <img
                                src={client.idCardBack}
                                alt="ID Card Back"
                                className="w-full h-48 object-cover rounded-lg border-2 border-amber-200 shadow-md"
                              />
                              <p className="text-xs text-center text-amber-700 mt-1 font-medium">Back</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </motion.div>

                  {/* Additional Information Section */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200"
                  >
                    <div className="flex items-center mb-4">
                      <Shield className="w-5 h-5 text-gray-600 mr-2" />
                      <h3 className="text-lg font-bold text-gray-900">Additional Information</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Client ID</label>
                        <div className="mt-1 flex items-center justify-between p-3 bg-white rounded-lg">
                          <p className="text-xs font-mono text-gray-600 truncate">
                            {client.slug || 'N/A'}
                          </p>
                          {client.slug && (
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => copyToClipboard(client.slug)}
                              className="ml-2 p-1 text-gray-600 hover:bg-gray-50 rounded flex-shrink-0"
                            >
                              <Copy className="w-3 h-3" />
                            </motion.button>
                          )}
                        </div>
                      </div>
                      {/* <div>
                        <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Registration Date</label>
                        <div className="mt-1 p-3 bg-white rounded-lg">
                          <p className="text-sm font-medium text-gray-900">
                            {formatDate(client.registrationDate)}
                          </p>
                        </div>
                      </div> */}
                    </div>
                  </motion.div>
                </div>
              </div>

              {/* Footer */}
              <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t border-gray-200">
                <div className="flex justify-end">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={onClose}
                    className="px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl hover:from-emerald-700 hover:to-teal-700 transition-all"
                  >
                    Close
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
